import HighlightedTextOutput from 'Components/Core/HighlightedTextOutput';
import { WorksheetCollatedMessagesButton } from 'Components/Worksheets/Core';
import { getMessagesForMultiplePaths } from 'Components/Worksheets/Core/Helpers/messageHelpers';
import { createDryMassEntryOptionSelector } from 'Components/Worksheets/MoistureContentWorksheet';
import { hasNonEmptyValue, hasValue } from 'Lib/Any';
import { Guid } from 'Lib/Guid';
import { pipe } from 'Lib/Hooks/Selectors';
import { splitIntoSearchTokens } from 'Lib/String/splitIntoSearchTokens';
import React, { useEffect, useState } from 'react';
import { useSelector } from 'react-redux';
import { useLocation, useNavigate } from 'react-router';
import { DryMassGridBalanceKey } from '.';
import { FormatTypes } from '../../../Lib/Format';
import styles from '../../../Styles/Util/_variables.module.scss';
import { Field, Message, NumericInput, TextOutput } from '../../Core';
import { withInjectedProps } from '../../Core/InjectedPropsContext';
import { WorksheetGridRequestButton, createFirstWorksheetSelector } from '../Core';
import WorksheetGrid from '../Core/WorksheetGrid/WorksheetGrid';
import { WorksheetGridWorksheetStatusIcon } from '../Core/WorksheetGridWorksheetStatusIcon';
import { WorksheetTopLevelLink } from '../Core/WorksheetTopLevelLink';
import { EntryComponent } from '../types';
import DryMassOtherAssignedTestSection from './DryMassOtherAssignedTestSection';

let DryMassWorksheetGridEntry: React.FC<EntryComponent> = ({ hidden = false }) => {
  const { state: locationState } = useLocation();
  const navigate = useNavigate();

  const [focusUUID, setFocusedUUID] = useState<Guid | null>(locationState?.focusUUID);

  /**
   * When the Grid has finished loading we want to clear the `focusUUID` so that we don't force focusing on the row after
   * subsequent changes to the grid i.e. refreshing the grid or filtering via the Sample ID. We also clear when the
   * local focusUUID is set to null to handle the case where you are on the grid entry page, scan a barcode and focus on a row.
   */
  useEffect(() => {
    if (focusUUID === null) navigate('.', { replace: true });
  }, [navigate, focusUUID]);

  /**
   * Set the focusUUID in the case where the user scans a barcode for a row while already on the grid entry page. This allows then
   * to use the barcode to find and focus on the row they want.
   */
  useEffect(() => {
    if (hasValue(locationState?.focusUUID)) {
      setFocusedUUID(locationState?.focusUUID);
    }
  }, [locationState]);
  /**
   * Currently all worksheets in the grid must come from the same laboratory
   * We assume that the laboratory data attached to the first worksheet applies to all worksheets.
   * (In future, we should fetch laboratory data independently from the worksheet model
   * so we have this whether or not a test is returned)
   */
  const showConstantDryMassEntryFields = useSelector(
    pipe(createDryMassEntryOptionSelector(createFirstWorksheetSelector), (o) => o !== 'Never')
  );

  /**
   * Paths of all fields we need to display messages for
   */
  const messagePaths = [
    `data.Equipment.DryMassBalance`,
    `data.MoistureContentMeasurements.ContainerLabel`,
    `data.DateTimePlacedInOvenLocal`,
    `data.MoistureContentMeasurements.ContainerMass`,
    'data.MoistureContentMeasurements.WetSoilMass',
    'data.MoistureContentMeasurements.WetSoilAndContainerMass',
    'data.Measurement.DrySoilAndContainerMass',
    'data.Measurement.DrySoilMass',
    'data.MoistureContentResults.MoistureContentPercent',
    ...(showConstantDryMassEntryFields
      ? [
          'data.Measurement.SoilAndContainerMassBeforeDrying',
          'data.Measurement.DryingTime',
          'data.Measurement.DateTimeRemovedFromOvenLocal',
        ]
      : []),
  ];

  const anyWorksheetLocked = useSelector((state) =>
    Object.values(state.worksheetGrid.worksheets).some((w) => w.status.readonly.locked)
  );

  const anyWorksheetHasMessages = useSelector((state) =>
    Object.values(state.worksheetGrid.worksheets).some(
      (w) => getMessagesForMultiplePaths(w.syncModel, messagePaths).length > 0
    )
  );

  const requiredCommonData = [
    'Measurement.Balance',
    ...(showConstantDryMassEntryFields ? ['Measurement.DateTimeRemovedFromOvenLocal'] : []),
  ];
  const readyForEntry = useSelector((state) =>
    requiredCommonData.every((k) => hasNonEmptyValue(state.worksheetGrid.commonData[k]))
  );

  const searchText = useSelector((state) => state.worksheetGrid.additionalData['SampleIdFilter']);

  const gridFields = [
    {
      field: <Field input={<TextOutput />} name={`data.MoistureContentMeasurements.ContainerLabel`} />,
      header: 'Tare ID',
      width: 140,
      sticky: true,
      cellStyle: { backgroundColor: styles.colourGrey300 },
    },
    ...(showConstantDryMassEntryFields
      ? [
          {
            field: (
              <Field
                input={<TextOutput />}
                name={`data.DateTimePlacedInOvenLocal`}
                format={{ type: FormatTypes.Time, pattern: 'YYYY-MM-DD HH:mm' }}
              />
            ),
            header: 'Time In Oven',
            width: 160,
            cellStyle: { backgroundColor: styles.colourGrey100 },
          },
        ]
      : [
          {
            field: (
              <Field
                input={<TextOutput />}
                name={`data.DateTimePlacedInOvenLocal`}
                format={{ type: FormatTypes.Date }}
              />
            ),
            header: 'Date In Oven',
            width: 100,
            cellStyle: { backgroundColor: styles.colourGrey100 },
          },
        ]),
    {
      field: <Field input={<TextOutput />} name={`data.MoistureContentMeasurements.ContainerMass`} />,
      header: 'Tare Mass (g)',
      width: 120,
      cellStyle: { backgroundColor: styles.colourGrey100 },
    },
    {
      field: (
        <Field
          input={<TextOutput />}
          name={`data.MoistureContentMeasurements.WetSoilAndContainerMass`}
          additionalMessagePaths={['data.MoistureContentMeasurements.WetSoilMass']}
        />
      ),
      header: 'Tare & Wet (g)',
      width: 120,
      cellStyle: { backgroundColor: styles.colourGrey100 },
    },
    {
      field: (worksheet: any) =>
        worksheet.syncModel.data.ReadingNumber > 1 ? (
          <Field
            input={<TextOutput />}
            name={`data.Measurement.SoilAndContainerMassBeforeDrying`}
            // We only store one dry mass balance for each test.
            // The balance selected for grid entry will end up being this balance when data is entered,
            // so we show the previous measurement as though it were measured with the current selected balance
            // NOTE: If we do not override this there is a bug where the readability changes while data is syncing
            measuredUsingStandaloneEquipment={DryMassGridBalanceKey}
          />
        ) : (
          // Don't show mass before drying when it was wet (it's already shown and this column is labelled as "dry" mass)
          <Field input={<TextOutput />} value={undefined} />
        ),
      header: 'Previous Tare & Dry (g)',
      width: 120,
      cellStyle: { backgroundColor: styles.colourGrey100 },
      hidden: !showConstantDryMassEntryFields,
    },
    {
      field: <Field input={<TextOutput />} name={`data.ReadingNumber`} />,
      header: 'Dry #',
      width: 40,
      cellStyle: { backgroundColor: styles.colourGrey100 },
      hidden: !showConstantDryMassEntryFields,
    },
    {
      field: (
        <Field
          input={<NumericInput />}
          name="data.Measurement.DrySoilAndContainerMass"
          disabled={!readyForEntry}
          measuredUsingStandaloneEquipment={DryMassGridBalanceKey}
        />
      ),
      header: 'Tare & Dry (g)',
      width: 140,
      autoFocus: (worksheetId: Guid) => {
        // return worksheetId === focusUuid; //) {
        if (worksheetId === focusUUID) {
          //   return true;
          // }
          // return false;
          setFocusedUUID(null);
          return true;
        }
        return false;
      },
    },
    {
      field: (worksheet: any) => <WorksheetGridWorksheetStatusIcon status={worksheet.status} />,
      header: '',
      width: 20,
      hidden: !anyWorksheetLocked,
      cellStyle: { padding: '0px', marginLeft: '-5px' },
    },
    {
      field: (worksheet: any) => (
        <WorksheetCollatedMessagesButton
          messagePaths={messagePaths}
          title={`Messages for ${worksheet?.syncModel?.header?.Sample?.SampleId} ${worksheet?.syncModel?.header?.ParentTest?.WorksheetType?.ShortName}`}
        />
      ),
      header: '',
      width: 30,
      hidden: !anyWorksheetHasMessages,
      cellStyle: { padding: '0px', paddingRight: '5px' },
    },
    {
      field: (worksheet: any) =>
        worksheet.syncModel.data.Measurement.IsSubsequentlyDried ? (
          <Message border={false}>
            <p style={{ fontStyle: 'italic' }}>Return to oven</p>
          </Message>
        ) : (
          <WorksheetGridRequestButton
            disabled={!hasValue(worksheet.syncModel.data.Measurement.DrySoilAndContainerMass?.value)}
            endpoint="soil.moistureContentTests.dryMassReadings.dryAgain"
            worksheetId={worksheet.syncModel.id}
          >
            Dry Again
          </WorksheetGridRequestButton>
        ),
      header: '',
      width: 100,
      hidden: !showConstantDryMassEntryFields,
      cellStyle: { padding: '0px', marginLeft: '-5px' },
    },
    {
      field: <Field input={<TextOutput />} name={`data.Measurement.DryingTime`} />,
      header: 'Drying Time (h)',
      width: 100,
      cellStyle: { backgroundColor: styles.colourGrey100 },
      hidden: !showConstantDryMassEntryFields,
    },
    {
      field: <Field input={<TextOutput />} name={`data.Measurement.DrySoilMass`} />,
      header: 'Dry Mass (g)',
      width: 120,
      cellStyle: { backgroundColor: styles.colourGrey100 },
    },
    {
      field: <Field input={<TextOutput />} name={`data.MoistureContentResults.MoistureContentPercent`} />,
      header: 'Moisture Content (%)',
      width: 120,
      cellStyle: { backgroundColor: styles.colourGrey100 },
    },
    {
      field: (_: any, worksheetPath: any) => <WorksheetTopLevelLink worksheetPath={worksheetPath} />,
      header: 'Part of',
      width: 160,
      cellStyle: { backgroundColor: styles.colourGrey100 },
    },
    {
      field: (
        <Field
          input={<HighlightedTextOutput />}
          name={`header.Sample.SampleId`}
          highlightSubstrings={splitIntoSearchTokens(searchText)}
        />
      ),
      header: 'Sample ID',
      width: 160,
      cellStyle: { backgroundColor: styles.colourGrey100 },
    },
    {
      field: (_: any, worksheetPath: any) => <DryMassOtherAssignedTestSection worksheetPath={worksheetPath} />,
      header: 'Other Assigned Tests',
      width: 160,
      cellStyle: { backgroundColor: styles.colourGrey100 },
    },
  ];

  return hidden ? null : <WorksheetGrid colDefinitions={gridFields} tabDirection={'vertical'} />;
};

DryMassWorksheetGridEntry = withInjectedProps(DryMassWorksheetGridEntry);

export default DryMassWorksheetGridEntry;
export { DryMassWorksheetGridEntry };
